import { Component } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslocoService } from "@ngneat/transloco";
import { combineLatest, interval, map, startWith } from "rxjs";
import { TimeSpanModel } from "source/app/models/time-span.model";
import { UtilityService } from "../../configuration/services/utility.service";
import { RideOpsUiService } from "../../configuration/services/rideops-ui.service";
import { LocationStateQuery } from "../../configuration/state/location-state.query";
import { CloseLocationComponent } from "../../modals/close-location/close-location.component";
import { DownLocationComponent } from "../../modals/down-location/down-location.component";
import { NoteEditorComponent, NoteEditorComponentMode } from "source/app/modals/note-editor/note-editor.component";

type HeaderScheduleSeverity = "default" | "warning" | "error";

interface HeaderScheduleStatus {
	timestamp: string;
	label: string | null;
	severity: HeaderScheduleSeverity;
	icon: "info" | "warning" | null;
}

@Component({
	selector: "app-current-user",
	templateUrl: "./current-user.component.html",
	styleUrls: ["./current-user.component.scss"],
})
export class CurrentUserComponent {
	constructor(
		private locationStateQuery: LocationStateQuery,
		private modalController: ModalController,
		private utilityService: UtilityService,
		private translocoService: TranslocoService,
		public rideOpsUi: RideOpsUiService,
	) {}

	operator$ = this.locationStateQuery.selectOperator$;
	locationName$ = this.locationStateQuery.locationName$;
	locationState$ = this.locationStateQuery.selectState$;
	noteTemplatesExist$ = this.locationStateQuery.noteTemplates$.pipe(map((templates) => templates?.length > 0));

	// Labels only on the operating dashboard (open + staffing/preopening cleared). Icon-only while
	// still on not-ready / preopening / ready-to-open chrome.
	showHeaderActionLabels$ = combineLatest([
		this.locationStateQuery.locationIsOpen$,
		this.locationStateQuery.isSafeToOperate$,
	]).pipe(map(([isOpen, isSafeToOperate]) => isOpen === true && isSafeToOperate === true));

	headerSchedule$ = combineLatest([
		interval(60000).pipe(startWith(0)),
		this.locationStateQuery.openingTime$,
		this.locationStateQuery.closingTime$,
		this.locationStateQuery.selectState$,
		this.locationStateQuery.isClearedForOperation$,
		this.locationStateQuery.stateLastUpdated$,
		this.translocoService.langChanges$.pipe(startWith(this.translocoService.getActiveLang())),
	]).pipe(
		map(([, openingTime, closingTime, state, isClearedForOperation, stateLastUpdated]) =>
			this.buildHeaderSchedule(
				openingTime,
				closingTime,
				state,
				isClearedForOperation === true,
				stateLastUpdated,
			),
		),
	);

	headerAvatars$ = combineLatest([this.operator$, this.locationStateQuery.positions$]).pipe(
		map(([operator, positions]) => {
			if (operator == null) {
				return [];
			}

			const attendants = (positions ?? [])
				.filter((position) => position.attendant?.user != null)
				.map((position) => ({
					id: position.attendant.user.id,
					name: position.attendant.user.name,
					profileImage: position.attendant.user.profileImage,
				}))
				.filter((attendant) => attendant.id !== operator.id)
				.slice(0, 2);

			return [
				...attendants,
				{ id: operator.id, name: operator.name, profileImage: operator.profileImage },
			];
		}),
	);

	trackByAvatarId = (_index: number, avatar: { id: string }) => avatar.id;

	positionsWithAttendant$ = this.locationStateQuery.positions$.pipe(
		map((positions) => {
			return positions.filter((position) => position.attendant != null);
		}),
	);

	timeLoggedIn$ = combineLatest([interval(1000).pipe(startWith(0)), this.operator$]).pipe(
		map(([_ticks, operator]) => {
			if (operator?.loggedIn == undefined) {
				return undefined;
			}

			const loggedIn = new Date(operator.loggedIn).getTime();
			const now = new Date().getTime();

			return TimeSpanModel.fromMinutes((now - loggedIn) / 60000);
		}),
	);

	closeLocation = async () => {
		const closeModal = await this.modalController.create({
			component: CloseLocationComponent,
			cssClass: "close-location-modal auto-height",
		});

		await closeModal.present();
	};

	startDowntime = async () => {
		const modal = await this.modalController.create({
			component: DownLocationComponent,
			cssClass: this.rideOpsUi.downLocationModalCssClass(),
		});

		await modal.present();
	};

	addNote = async () => {
		const modal = await this.modalController.create({
			component: NoteEditorComponent,
			cssClass: this.rideOpsUi.noteEditorModalCssClass(NoteEditorComponentMode.Create),
			componentProps: {
				mode: NoteEditorComponentMode.Create,
			},
		});

		await modal.present();
	};

	openStaffOverview = async () => {
		const { StaffOverviewComponent } = await import(
			"../../features/operators/modals/staff-overview/staff-overview.component"
		);
		const modal = await this.modalController.create({
			component: StaffOverviewComponent,
			cssClass: "staff-overview-modal-v2",
		});

		await modal.present();
	};

	private buildHeaderSchedule = (
		openingTime: string | undefined,
		closingTime: string | undefined,
		state: "Open" | "Closed" | "Down",
		isClearedForOperation: boolean,
		stateLastUpdated: string | undefined,
	): HeaderScheduleStatus => {
		const timestamp = this.utilityService.formatDateTime(new Date()) ?? "";
		const useClosing = state === "Open" || state === "Down";
		const targetTime = useClosing ? closingTime : openingTime;

		if (targetTime == null || targetTime === "") {
			return { timestamp, label: null, severity: "default", icon: null };
		}

		const target = new Date(targetTime);
		if (Number.isNaN(target.getTime())) {
			return { timestamp, label: null, severity: "default", icon: null };
		}

		// Closed after today's closing window: don't keep showing opening overdue.
		if (!useClosing && closingTime) {
			const closing = new Date(closingTime);
			if (!Number.isNaN(closing.getTime()) && Date.now() > closing.getTime()) {
				return { timestamp, label: null, severity: "default", icon: null };
			}
		}

		const diffMs = target.getTime() - Date.now();
		const oneHourMs = 60 * 60 * 1000;
		const timeLabel = this.utilityService.formatTime(target);

		if (diffMs > oneHourMs) {
			return {
				timestamp,
				label: this.translocoService.translate(
					useClosing ? "V2_HEADER__PLANNED_CLOSING" : "V2_HEADER__PLANNED_OPENING",
					{ time: timeLabel },
				),
				severity: "default",
				icon: null,
			};
		}

		if (diffMs > 0) {
			const minutes = Math.max(1, Math.ceil(diffMs / 60000));
			if (useClosing) {
				return {
					timestamp,
					label: this.translocoService.translate("V2_HEADER__CLOSING_IN", { minutes }),
					severity: "warning",
					icon: "info",
				};
			}

			return {
				timestamp,
				label: this.translocoService.translate(
					isClearedForOperation ? "V2_HEADER__OPENING_IN" : "V2_HEADER__OPENING_IN_NOT_READY",
					{ minutes },
				),
				severity: "warning",
				icon: "info",
			};
		}

		// Already opened (then closed) after today's opening — don't show opening overdue again.
		if (!useClosing && stateLastUpdated) {
			const lastUpdated = new Date(stateLastUpdated);
			if (!Number.isNaN(lastUpdated.getTime()) && lastUpdated.getTime() >= target.getTime()) {
				return { timestamp, label: null, severity: "default", icon: null };
			}
		}

		const totalMinutes = Math.max(1, Math.ceil(Math.abs(diffMs) / 60000));
		const overdueKey = useClosing
			? totalMinutes > 60
				? "V2_HEADER__CLOSING_OVERDUE_HOURS"
				: "V2_HEADER__CLOSING_OVERDUE"
			: isClearedForOperation
				? totalMinutes > 60
					? "V2_HEADER__OPENING_OVERDUE_HOURS"
					: "V2_HEADER__OPENING_OVERDUE"
				: totalMinutes > 60
					? "V2_HEADER__OPENING_OVERDUE_NOT_READY_HOURS"
					: "V2_HEADER__OPENING_OVERDUE_NOT_READY";
		return {
			timestamp,
			label: this.translocoService.translate(
				overdueKey,
				totalMinutes > 60
					? { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 }
					: { minutes: totalMinutes },
			),
			severity: "error",
			icon: "warning",
		};
	};
}
